/**
 * 咨询聊天 WebSocket (B-1B)
 *
 * 后端协议: docs/api/legal-websocket-api.md §3
 * 端点: /ws/client/legal/consult/chat
 *
 * 上行 action: ping / send / mark_read / typing / recall / ack
 * 下行 event:
 *   message_push / message_ack / message_delivered / message_read /
 *   message_recalled / typing / presence_change /
 *   offline_messages / offline_sync_complete / auth_error / error
 */
import { WsClient } from '@/lib/ws'

/* ---------- 下行 data 类型 (按 §3.3) ---------- */

export interface WsConsultMessage {
  id: string
  sessionId: string
  senderId: string
  /** 1 用户 / 2 律师 / 3 系统 */
  senderType: string
  receiverId: string
  /** 1 文本 / 2 图片 / 3 语音 / 5 文件 */
  msgType: string | number
  content?: string | null
  mediaUrl?: string | null
  ossId?: string | null
  mediaDuration?: string | number | null
  fileName?: string | null
  fileSize?: string | number | null
  isRead?: string | null
  readTime?: string | null
  isRecalled?: string | null
  recallTime?: string | null
  quoteMsgId?: string | null
  /** 0 未送达/等待接收方 ACK / 1 已送达 / 2 发送失败 */
  status?: string | null
  createTime?: string | number | null
  updateTime?: string | null
  /** incoming / outgoing — 仅部分推送携带 */
  direction?: 'incoming' | 'outgoing'
}

export interface WsMessageAckData {
  message: WsConsultMessage
  accepted: boolean
  deliveryStatus: string
  recipientOnline: boolean
  clientMsgId?: string
}

export interface WsOfflineMessagesData {
  messages: WsConsultMessage[]
}

export interface WsOfflineSyncCompleteData {
  count: number
  timestamp: number
}

export interface WsMarkReadAckData {
  sessionId: string
  messageIds: string[]
  readerId: string
  timestamp: number
}

export interface WsMessageReadData extends WsMarkReadAckData {
  actorId?: string
}

export interface WsTypingData {
  sessionId: string
  userId: string
  typing: boolean
  timestamp: number
}

export interface WsMessageRecalledData {
  sessionId: string
  messageId: string
  operatorId: string
  timestamp: number
  msgType?: string
}

export interface WsMessageRecallAckData {
  sessionId: string
  messageId: string
  operatorId: string
  timestamp: number
}

export interface WsMessageDeliveredData {
  messageId: string
  sessionId: string
  receiverId: string
  timestamp: number
}

export interface WsPresenceChangeData {
  userId: string
  userType?: string
  online: boolean
  timestamp: number
}

export interface WsContactExchangeEventData {
  exchangeId?: string
  chatMessageId?: string
  messageId?: string
  sessionId?: string
  initiatorId?: string
  receiverId?: string
  status?: 'pending' | 'agreed' | 'rejected' | 'canceled' | 'expired' | string
  expireTime?: string | number | null
  initiatorTypes?: string[]
  receiverTypes?: string[]
}

export interface WsErrorData {
  error: string
  message?: string
  code?: string | number
  timestamp?: number
}

/* ---------- 上行 helper ---------- */

/** 消息类型: 1 文本 / 2 图片 / 3 语音 / 5 文件 / 7 联系方式交换成功 */
export const CONSULT_MSG_TYPE = {
  TEXT: 1,
  IMAGE: 2,
  VOICE: 3,
  FILE: 5,
  CONTACT_EXCHANGE: 7,
} as const

export interface SendChatMessageInput {
  sessionId: string
  msgType: number
  sourceClientType?: 'WEB' | string
  content?: string
  mediaUrl?: string
  ossId?: string
  mediaDuration?: number
  fileName?: string
  fileSize?: number | string
  quoteMsgId?: string
  clientMsgId?: string
}

/* ---------- 单例客户端 ---------- */

const client = new WsClient({
  path: '/ws/client/legal/consult/chat',
  debugName: 'consult-ws',
})

export function getConsultChatWs(): WsClient {
  return client
}

/** 发送文本 / 图片 / 语音 / 文件消息 */
export function sendChatMessage(input: SendChatMessageInput): void {
  client.send({ action: 'send', sourceClientType: 'WEB', ...input })
}

/** 批量标记已读 (3-5s 合并一次) */
export function markChatRead(sessionId: string, messageIds: string[]): void {
  if (messageIds.length === 0) return
  client.send({ action: 'mark_read', sessionId, messageIds })
}

/** 输入中状态 (前端 ≤ 1/2s) */
export function sendChatTyping(sessionId: string, typing: boolean): void {
  client.send({ action: 'typing', sessionId, typing })
}

/** 撤回消息 */
export function recallChatMessage(sessionId: string, messageId: string): void {
  client.send({ action: 'recall', sessionId, messageId })
}

/** 接收方确认送达 */
export function ackChatMessage(messageId: string): void {
  client.send({ action: 'ack', messageId })
}
